import { useState, useRef, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useHdmgdjSeo } from "@/hooks/hdmgdj/useHdmgdjSeo";
import { hdmgdjSeo } from "@/config/hdmgdjSeo";

const reportTypes = [
  { id: "product", label: "产品侵权" },
  { id: "fake", label: "虚假官网" },
  { id: "porn", label: "低俗色情" },
  { id: "gambling", label: "赌博博彩" },
  { id: "violence", label: "暴力恐怖" },
  { id: "fraud", label: "欺诈交易" },
  { id: "virus", label: "病毒木马" },
  { id: "other", label: "其他情况" },
];

function validateContact(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "请填写联系方式";
  const phoneRegex = /^1[3-9]\d{9}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const qqRegex = /^\d{5,12}$/;
  if (phoneRegex.test(trimmed) || emailRegex.test(trimmed) || qqRegex.test(trimmed)) {
    return null;
  }
  return "请输入有效的手机号、邮箱或QQ号";
}

export default function HdmgdjReport() {
  const [selectedType, setSelectedType] = useState("");
  const [description, setDescription] = useState("");
  const [contact, setContact] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  useHdmgdjSeo(hdmgdjSeo.report);

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!selectedType) {
      errors.report_type = "请选择投诉类型";
    }
    if (!description.trim()) {
      errors.description = "请填写问题描述";
    }
    const contactError = validateContact(contact);
    if (contactError) {
      errors.contact = contactError;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    if (!validateForm()) {
      return;
    }
    setStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = ((formData.get("website_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setDescription("");
      setContact("");
      setSelectedType("");
      setFieldErrors({});
      return;
    }
    formData.delete("website_alt");
    try {
      const response = await fetch("https://formspree.io/f/mqpkqdlo", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (response.ok) {
        setStatus("success");
        form.reset();
        setDescription("");
        setContact("");
        setSelectedType("");
        setFieldErrors({});
      } else {
        setStatus("error");
        setErrorMsg("提交失败，请稍后重试");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("网络异常，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col items-center py-16 px-4">
      <div className="text-center mb-8">
        <h1 className="text-[28px] font-bold text-[#333] mb-2">
          投诉举报
        </h1>
        <p className="text-[14px] text-[#999]">
          请如实填写以下信息，我们会尽快核实处理。
        </p>
      </div>

      {/* 表单卡片 */}
      <div className="w-full max-w-[720px] bg-white rounded-[8px] px-8 py-10">
        {status === "success" ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 rounded-full bg-[#1890ff] flex items-center justify-center mb-4">
              <i className="ri-check-line text-2xl text-white" />
            </div>
            <h3 className="text-lg font-bold text-[#333] mb-1">提交成功</h3>
            <p className="text-sm text-[#999] mb-8">我们已收到您的投诉，会尽快核实处理。</p>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-2 bg-[#1890ff] text-white text-[14px] rounded-[4px] hover:bg-[#40a9ff] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              返回
            </button>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} action="https://formspree.io/f/mqpkqdlo" method="POST" noValidate>
            <div className="mb-7">
              <label className="block text-[14px] font-bold text-[#333] mb-3">
                <span className="text-[#ff4d4f] mr-1">*</span>投诉类型
              </label>
              <div className="flex flex-wrap gap-5">
                {reportTypes.map((type) => (
                  <label
                    key={type.id}
                    className="inline-flex items-center gap-2 cursor-pointer select-none"
                  >
                    <input
                      type="radio"
                      name="report_type"
                      value={type.label}
                      checked={selectedType === type.id}
                      onChange={() => {
                        setSelectedType(type.id);
                        if (fieldErrors.report_type) {
                          setFieldErrors((prev) => { const n = { ...prev }; delete n.report_type; return n; });
                        }
                      }}
                      className="w-4 h-4 accent-[#1890ff] cursor-pointer"
                    />
                    <span className="text-[14px] text-[#333]">{type.label}</span>
                  </label>
                ))}
              </div>
              {fieldErrors.report_type && (
                <p className="text-[13px] text-[#ff4d4f] mt-2">{fieldErrors.report_type}</p>
              )}
            </div>

            <div className="mb-7">
              <label className="block text-[14px] font-bold text-[#333] mb-2">
                <span className="text-[#ff4d4f] mr-1">*</span>问题描述
              </label>
              <p className="text-[12px] text-[#999] mb-2">
                请详细描述遇到的问题、举报的理由以及期望得到的处理结果
              </p>
              <textarea
                name="description"
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setDescription(e.target.value);
                  }
                  if (fieldErrors.description && e.target.value.trim()) {
                    setFieldErrors((prev) => { const n = { ...prev }; delete n.description; return n; });
                  }
                }}
                placeholder="请详细描述..."
                rows={6}
                maxLength={500}
                className={`w-full px-3 py-2 border rounded-[4px] bg-white text-[14px] text-[#333] placeholder:text-[#bfbfbf] focus:outline-none transition-all resize-none ${
                  fieldErrors.description
                    ? "border-[#ff4d4f] focus:border-[#ff4d4f] focus:shadow-[0_0_0_2px_rgba(255,77,79,0.2)]"
                    : "border-[#d9d9d9] focus:border-[#40a9ff] focus:shadow-[0_0_0_2px_rgba(24,144,255,0.2)]"
                }`}
              />
              {fieldErrors.description && (
                <p className="text-[13px] text-[#ff4d4f] mt-1">{fieldErrors.description}</p>
              )}
            </div>

            <div className="mb-8">
              <label className="block text-[14px] font-bold text-[#333] mb-2">
                <span className="text-[#ff4d4f] mr-1">*</span>联系方式
              </label>
              <p className="text-[12px] text-[#999] mb-2">
                请留下您的邮箱 / 手机 / QQ 号
              </p>
              <input
                type="text"
                name="contact"
                value={contact}
                onChange={(e) => {
                  setContact(e.target.value);
                  if (fieldErrors.contact && !validateContact(e.target.value)) {
                    setFieldErrors((prev) => { const n = { ...prev }; delete n.contact; return n; });
                  }
                }}
                placeholder="邮箱、手机或QQ"
                className={`w-full px-3 py-2 border rounded-[4px] bg-white text-[14px] text-[#333] placeholder:text-[#bfbfbf] focus:outline-none transition-all ${
                  fieldErrors.contact
                    ? "border-[#ff4d4f] focus:border-[#ff4d4f] focus:shadow-[0_0_0_2px_rgba(255,77,79,0.2)]"
                    : "border-[#d9d9d9] focus:border-[#40a9ff] focus:shadow-[0_0_0_2px_rgba(24,144,255,0.2)]"
                }`}
              />
              {fieldErrors.contact && (
                <p className="text-[13px] text-[#ff4d4f] mt-1">{fieldErrors.contact}</p>
              )}
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hdmgdj-honeypot"
            />

            {status === "error" && errorMsg && (
              <div className="mb-5 p-3 border border-[#ff4d4f] bg-[#fff2f0] rounded-[4px] text-[13px] text-[#ff4d4f]">
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full sm:w-auto px-8 py-2.5 bg-[#1890ff] text-white text-[14px] rounded-[4px] hover:bg-[#40a9ff] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {status === "submitting" ? "提交中..." : "提交举报"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}